import { storageService, dbService } from '../firebaseInstance';

/**
 * 사용자 ID로 예측 가능한 Public Avatar URL 생성
 * @param {string} userID - 사용자 ID (Threads userID)
 * @param {number} size - 이미지 크기 (픽셀, 옵션)
 * @returns {string} Public Avatar URL
 */
export const getPublicAvatarUrl = (userID, size = 160) => {
  if (!userID) {
    return null;
  }
  
  const bucketName = 'my-threads-insight.firebasestorage.app';
  // Firebase Storage 공개 URL 형식 사용 (크기 조정 매개변수 추가)
  const publicUrl = `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o/avatars%2F${userID}.jpg?alt=media&w=${size}&h=${size}`;
  
  return publicUrl;
};

/**
 * URL 해시 생성 (이미지 변경 감지용)
 * @param {string} url - 해시할 URL
 * @returns {Promise<string>} URL 해시
 */
const generateUrlHash = async (url) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(url);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * 아바타 메타데이터를 user 컬렉션에 저장
 * @param {string} userID - 사용자 ID
 * @param {string} threadsProfileUrl - Threads 프로필 이미지 URL
 */
const saveAvatarMetadata = async (userID, threadsProfileUrl) => {
  try {
    const urlHash = await generateUrlHash(threadsProfileUrl);
    
    await dbService.collection('user').doc(userID).update({
      avatarMetadata: {
        originalUrl: threadsProfileUrl,
        urlHash: urlHash,
        uploadedAt: Date.now(),
        lastChecked: Date.now(),
        storageFileName: `${userID}.jpg`,
        publicUrl: getPublicAvatarUrl(userID)
      }
    });
    
    console.log(`💾 [AVATAR METADATA] Saved metadata for user: ${userID}`);
  } catch (error) {
    console.warn(`⚠️ [AVATAR METADATA] Failed to save metadata for user: ${userID}`, error);
  }
};

/**
 * 아바타 메타데이터 가져오기
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Object|null>} 아바타 메타데이터
 */
const getAvatarMetadata = async (userID) => {
  try {
    const userDoc = await dbService.collection('user').doc(userID).get();
    return userDoc.exists ? userDoc.data().avatarMetadata : null;
  } catch (error) {
    console.warn(`⚠️ [AVATAR METADATA] Failed to get metadata for user: ${userID}`, error);
    return null;
  }
};

/**
 * 아바타 업데이트 필요 여부 확인
 * @param {string} userID - 사용자 ID
 * @param {string} threadsProfileUrl - Threads 프로필 이미지 URL
 * @returns {Promise<boolean>} 업데이트 필요 여부
 */
const checkAvatarNeedsUpdate = async (userID, threadsProfileUrl) => {
  try {
    console.log(`🔍 [AVATAR CHECK] Checking if update needed for user: ${userID}`);
    
    // 1. 기존 메타데이터 확인
    const metadata = await getAvatarMetadata(userID);
    
    if (!metadata) {
      console.log(`📝 [AVATAR CHECK] No metadata found for user: ${userID} - needs upload`);
      return true;
    }
    
    // 2. URL 해시 비교 (이미지 변경 감지)
    const currentUrlHash = await generateUrlHash(threadsProfileUrl);
    
    if (metadata.urlHash !== currentUrlHash) {
      console.log(`🔄 [AVATAR CHECK] URL changed for user: ${userID} - needs update`);
      console.log(`🔄 [AVATAR CHECK] Old hash: ${metadata.urlHash?.substring(0, 8)}...`);
      console.log(`🔄 [AVATAR CHECK] New hash: ${currentUrlHash.substring(0, 8)}...`);
      return true;
    }
    
    // 3. Storage에 실제 파일 존재 확인
    const fileExists = await checkAvatarExists(userID);
    if (!fileExists) {
      console.log(`📄 [AVATAR CHECK] Storage file missing for user: ${userID} - needs upload`);
      return true;
    }
    
    // 4. 마지막 확인 시간 업데이트 (24시간마다)
    const lastChecked = metadata.lastChecked || 0;
    const checkInterval = 24 * 60 * 60 * 1000; // 24시간
    
    if (Date.now() - lastChecked > checkInterval) {
      // lastChecked만 업데이트
      await dbService.collection('user').doc(userID).update({
        'avatarMetadata.lastChecked': Date.now()
      });
      console.log(`⏰ [AVATAR CHECK] Updated lastChecked for user: ${userID}`);
    }
    
    console.log(`✅ [AVATAR CHECK] Avatar up-to-date for user: ${userID}`);
    return false;
    
  } catch (error) {
    console.warn(`⚠️ [AVATAR CHECK] Check failed for user: ${userID}, will upload`, error);
    return true; // 확인 실패 시 안전하게 업로드
  }
};

/**
 * 사용자 아바타를 Storage에 저장하고 Public으로 설정 (조건부)
 * @param {string} userID - 사용자 ID (Threads userID)
 * @param {string} threadsProfileUrl - Threads 프로필 이미지 URL
 * @param {boolean} forceUpdate - 강제 업데이트 여부
 * @returns {Promise<string>} Public Avatar URL
 */
export const uploadAndMakePublicAvatar = async (userID, threadsProfileUrl, forceUpdate = false) => {
  try {
    console.log(`🔄 [AVATAR CHECK] User: ${userID}, Force: ${forceUpdate}`);
    console.log(`📷 [AVATAR CHECK] Source URL: ${threadsProfileUrl}`);
    
    // 1. 강제 업데이트가 아닌 경우, 기존 아바타 확인
    if (!forceUpdate) {
      const needsUpdate = await checkAvatarNeedsUpdate(userID, threadsProfileUrl);
      if (!needsUpdate) {
        const publicUrl = getPublicAvatarUrl(userID);
        console.log(`✅ [AVATAR SKIP] Avatar up-to-date for user: ${userID}`);
        return publicUrl;
      }
    }
    
    console.log(`🔄 [AVATAR UPLOAD START] User: ${userID}`);
    
    // 2. 이미지 다운로드
    console.log(`📥 [AVATAR UPLOAD] Downloading image from Threads...`);
    const imageRes = await fetch(threadsProfileUrl, {
      mode: 'cors',
      headers: {
        'User-Agent': 'MyThreadsInsight/1.0'
      }
    });
    
    if (!imageRes.ok) {
      throw new Error(`HTTP ${imageRes.status}: ${imageRes.statusText}`);
    }
    
    const blob = await imageRes.blob();
    console.log(`📥 [AVATAR UPLOAD] Image downloaded successfully. Size: ${Math.round(blob.size / 1024)}KB`);
    
    // 3. Firebase Storage에 업로드 (Threads userID 사용)
    const fileName = `${userID}.jpg`;
    const ref = storageService.ref(`avatars/${fileName}`);
    console.log(`📤 [AVATAR UPLOAD] Uploading to Firebase Storage: avatars/${fileName}`);
    
    const metadata = {
      contentType: 'image/jpeg',
      cacheControl: 'public,max-age=31536000', // 1년 캐시
      customMetadata: {
        'originalUrl': threadsProfileUrl,
        'uploadedAt': Date.now().toString(),
        'userID': userID
      }
    };
    
    await ref.put(blob, metadata);
    console.log(`📤 [AVATAR UPLOAD] Upload to Firebase Storage completed`);
    
    // 4. user 컬렉션에 아바타 메타데이터 저장
    await saveAvatarMetadata(userID, threadsProfileUrl);
    
    // 5. 예측 가능한 Public URL 반환
    const publicUrl = getPublicAvatarUrl(userID);
    console.log(`🎯 [AVATAR UPLOAD SUCCESS] Public URL generated: ${publicUrl}`);
    console.log(`✅ [AVATAR UPLOAD COMPLETE] Avatar uploaded and made public for user: ${userID}`);
    
    return publicUrl;
    
  } catch (error) {
    console.error(`❌ [AVATAR UPLOAD FAILED] User: ${userID}, Error:`, error);
    throw error;
  }
};

/**
 * 아바타 이미지 존재 여부 확인 (Firebase Storage SDK 사용)
 * @param {string} userID - 사용자 ID
 * @returns {Promise<boolean>} 이미지 존재 여부
 */
export const checkAvatarExists = async (userID) => {
  try {
    const fileName = `${userID}.jpg`;
    const ref = storageService.ref(`avatars/${fileName}`);
    
    // Firebase Storage getMetadata로 파일 존재 확인
    await ref.getMetadata();
    return true;
  } catch (error) {
    // 파일이 없으면 404 에러 발생
    if (error.code === 'storage/object-not-found') {
      return false;
    }
    // 다른 에러는 로그 출력 후 false 반환
    console.warn(`⚠️ [AVATAR EXISTS] Check failed for user: ${userID}`, error);
    return false;
  }
};

/**
 * 사용자 이름의 첫 글자를 가져와 Avatar fallback 텍스트 생성
 * @param {string} username - 사용자 이름
 * @returns {string} 첫 글자 (대문자)
 */
export const getAvatarFallbackText = (username) => {
  if (!username) return '?';
  return username.charAt(0).toUpperCase();
};